import { SyntheticEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import {
  BillToLocationFormField,
  LocationFormField,
  ShipToLocationFormField,
} from 'contacts/elements';
import { IContactGroup, IContactGroupAddress } from 'contacts/types';
import { getLocationRequestPayloadByAlternateVersion } from 'contacts/utils';
import { AlternateVersionHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { IAutocompleteOption, IAutocompleteRef } from 'core/types';

interface IProps {
  contactGroup: Partial<IContactGroup>;
  level: number;
}

export const DefaultLocationsFormFields = ({ contactGroup, level }: IProps) => {
  const { t } = useTranslation();

  const billToRef = useRef<IAutocompleteRef>(null);
  const shipToRef = useRef<IAutocompleteRef>(null);
  const soldToRef = useRef<IAutocompleteRef>(null);

  const { user } = useAuthUser();
  const { setValue } = useFormContext();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const refetchAll = useCallback(() => {
    billToRef.current?.refetch();
    shipToRef.current?.refetch();
    soldToRef.current?.refetch();
  }, [billToRef, shipToRef, soldToRef]);

  const onBillToLocationChange = useCallback(
    (event: SyntheticEvent, option: IContactGroupAddress | IContactGroupAddress[]) => {
      if (isAlternateVersionEnabled) {
        const billToValue = Array.isArray(option) ? option[0] : option;
        setValue('soldTo', billToValue || null);
      }
    },
    [isAlternateVersionEnabled, setValue]
  );

  const onCreateNewLocation = useCallback(
    (location: IAutocompleteOption) => {
      if (location?.id) {
        refetchAll();
      }
    },
    [refetchAll]
  );

  const onCreateNewBillToLocation = useCallback(
    (location: IAutocompleteOption) => {
      onCreateNewLocation(location);
      onBillToLocationChange(null, location as IContactGroupAddress);
    },
    [onBillToLocationChange, onCreateNewLocation]
  );

  const billToRequestPayload = getLocationRequestPayloadByAlternateVersion(
    {},
    contactGroup?.id,
    isAlternateVersionEnabled
  );

  const shipToRequestPayload = getLocationRequestPayloadByAlternateVersion(
    {},
    contactGroup?.id,
    isAlternateVersionEnabled
  );

  return (
    <>
      <BillToLocationFormField
        ref={billToRef}
        key="upsertContactBillTo"
        name="billTo"
        label={t('labelDefaultBillTo')}
        contactGroup={contactGroup}
        requestPayload={billToRequestPayload}
        onCreateNewOption={onCreateNewBillToLocation}
        onChange={onBillToLocationChange}
        level={level + 1}
        groupOptionsByContactGroup={isAlternateVersionEnabled}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      <ShipToLocationFormField
        ref={shipToRef}
        key="upsertContactShipTo"
        name="shipTo"
        label={t('labelDefaultShipTo')}
        contactGroup={contactGroup}
        requestPayload={shipToRequestPayload}
        onCreateNewOption={onCreateNewLocation}
        level={level + 1}
        groupOptionsByContactGroup={isAlternateVersionEnabled}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      {!isAlternateVersionEnabled && (
        <LocationFormField
          ref={soldToRef}
          key="upsertContactSoldTo"
          name="soldTo"
          label={t('labelDefaultSoldTo')}
          contactGroup={contactGroup}
          requestPayload={{
            filters: {
              contactGroupId: contactGroup?.id,
            },
          }}
          onCreateNewOption={onCreateNewLocation}
          level={level + 1}
        />
      )}
    </>
  );
};
